import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type HealthSignal } from '@apm/contracts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { RagBadge } from '../../../components/ui/rag-badge';
import { errorMessage } from '../../../lib/error-message';
import { dashboardApi, dashboardKeys } from '../api';

/**
 * Health của một dự án — tầng PM của spec 16.5.
 *
 * Cùng phép tính, cùng ba tín hiệu như bảng của lãnh đạo. Đó là chủ đích: PM và
 * lãnh đạo phải nhìn **cùng một màu** về cùng một dự án, nếu không thì mỗi buổi
 * review sẽ bắt đầu bằng việc tranh nhau xem con số nào đúng.
 */
export function ProjectHealthCard({
  projectId,
  asOf,
  showName = false,
}: {
  projectId: string;
  asOf?: string | undefined;
  /**
   * Tiêu đề là tên dự án thay vì "Tình trạng dự án" — dùng ở trang chủ, nơi
   * nhiều thẻ đứng cạnh nhau và tiêu đề chung thì không nói thẻ nào của ai.
   */
  showName?: boolean;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: dashboardKeys.projectHealth(projectId, asOf ?? ''),
    queryFn: () => dashboardApi.projectHealth(projectId, asOf),
  });

  const data = report.data;
  const signalOf = (code: HealthSignal['code']): HealthSignal =>
    data?.signals.find((signal) => signal.code === code) ?? {
      code,
      status: 'unknown',
      value: null,
    };

  return (
    <ChartFrame
      title={showName && data ? `${data.key} · ${data.name}` : t('dashboard.projectHealthTitle')}
      help="projectHealth"
      {...(data
        ? { subtitle: `${data.doneCount}/${data.taskCount} ${t('dashboard.taskUnit')}` }
        : {})}
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      {...(data ? { actions: <RagBadge status={data.overall} /> } : {})}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <SignalCard
          title={t('dashboard.signalSchedule')}
          signal={signalOf('schedule')}
          detail={t('dashboard.lateTasks', { count: data?.lateTaskCount ?? 0 })}
        />
        <SignalCard
          title={t('dashboard.signalRisk')}
          signal={signalOf('risk')}
          detail={t('dashboard.openRisks', { count: data?.openRiskCount ?? 0 })}
        />
        <SignalCard
          title={t('dashboard.signalProgress')}
          signal={signalOf('progress')}
          detail={
            data?.nextMilestone
              ? `${data.nextMilestone.date} · ${data.nextMilestone.title}`
              : t('dashboard.noMilestone')
          }
        />
      </div>
    </ChartFrame>
  );
}

function SignalCard({
  title,
  signal,
  detail,
}: {
  title: string;
  signal: HealthSignal;
  detail: string;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1 rounded-DEFAULT border border-border p-3">
      <p className="text-xs text-muted-foreground">{title}</p>
      {/* Con số quyết định màu nằm ngay cạnh màu — xem `PortfolioTable`.
          `self-start` để chip không giãn hết chiều ngang thẻ. */}
      <RagBadge
        status={signal.status}
        label={signal.value === null ? '—' : String(signal.value)}
        className="self-start"
      />
      <p className="truncate text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}
